import * as vp from 'vopay';
import {
  Table,
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetter,
  getMaskedNumber,
  usePagination,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { format, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { PaymentMethodLogo } from '#shared/components/UI';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { useHostNavigate } from '../HostNavigationContext';
import useUpdateHostQueryParams from '#shared/hooks/useUpdateHostQueryParams';
import useFileDownload from '#shared/utils/useFileDownload';
import useFetchAccountVerification from '../hooks/useFetchAccountVerification';
import BankVerifyHistoryForm from './BankVerifyHistoryForm';
import BankVerifyHistoryExport from './BankVerifyHistoryExport';
import ActivityType, { ActivityTabType } from './ActivityType';
import type { ActivitySubTableProps } from './TransactionHistoryTable';

import { Button, Skeleton, TableFiltersContainer, TransactionStatus } from '#shared/components/UI';
import toDayTimestamp from './toDayTimestamp';

type QueryParams = {
  status?: string;
  verificationStatus?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  activityType?: ActivityTabType;
};

type Filters = {
  status: string;
  verificationStatus: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
};

type TransactionParams = Filters & {
  hasInvalidEndDate: boolean;
  offset?: number;
};

// The portal's Customer Activity → Bank Verify History table, adapted for the
// contact drawer/portal pages: filters live in memory (mirrored to the URL
// when persistFiltersInUrl) and the verification-details link-out goes
// through the host's onNavigate (hidden when the host can't navigate).
const BankVerifyHistoryTable: FunctionComponent<ActivitySubTableProps> = ({
  clientAccountId,
  clientAccountsEnabled,
  activityTypeOptions,
  currentType,
  handleCurrentType,
  persistFiltersInUrl,
  timezone,
}) => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();

  const { t } = useTranslation('translations', { keyPrefix: 'Shared.BankVerifyHistory' });
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const hostNavigate = useHostNavigate();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const { fetching, fetchAccountVerification, totalRecords } = useFetchAccountVerification();
  const { fetchAccountVerification: fetchAccountVerificationExport } = useFetchAccountVerification();
  const [filteredList, setFilteredList] = useState<vp.VerificationRequestsList[]>([]);
  const [exportList, setExportList] = useState<vp.VerificationRequestsList[]>([]);
  const [columnSwap, setColumnSwap] = useState<'name' | 'email'>('name');

  const defaultTimeZone = timezone || 'UTC';

  const initialFilters: Filters = {
    status: '',
    verificationStatus: '',
    searchTerm: '',
    startDate: formatInTimeZone(subDays(new Date(), 7), defaultTimeZone, 'yyyy-MM-dd'),
    endDate: formatInTimeZone(new Date(), defaultTimeZone, 'yyyy-MM-dd'),
    timeZone: defaultTimeZone,
  };

  // Portal mode seeds the filters from the page URL (deep links, reload/back
  // restoration); the drawer starts from the in-memory defaults.
  const [filters, setFilters] = useState<Filters>(() => {
    if (!persistFiltersInUrl) return initialFilters;

    const urlParams = new URLSearchParams(window.location.search);
    const timeZone = urlParams.get('timeZone') || defaultTimeZone;

    return {
      status: urlParams.get('status') || '',
      verificationStatus: urlParams.get('verificationStatus') || '',
      searchTerm: urlParams.get('searchTerm') || '',
      startDate: urlParams.get('startDate') || formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'),
      endDate: urlParams.get('endDate') || formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'),
      timeZone,
    };
  });

  const updateQueryParams = useUpdateHostQueryParams<QueryParams>();

  const filtersApplied = Boolean(
    filters.status ||
      filters.verificationStatus ||
      filters.searchTerm ||
      filters.startDate !== initialFilters.startDate ||
      filters.endDate !== initialFilters.endDate ||
      filters.timeZone !== initialFilters.timeZone,
  );

  // pagination
  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(totalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchBankVerifyHistory = async ({
    startDate,
    endDate,
    status,
    verificationStatus,
    hasInvalidEndDate,
    searchTerm,
    timeZone,
    offset = 0,
  }: TransactionParams) => {
    const bankVerifyHistoryRequest = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: timeZone,
      RequestStatus: status,
      SearchText: searchTerm,
      VerificationStatus: verificationStatus,
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: clientAccountId,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    try {
      const res = await fetchAccountVerification(bankVerifyHistoryRequest);

      const verificationRequest = Object.values(res.VerificationRequests || {});
      setFilteredList(verificationRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching bank verify history';
      alertDialog.error('Error', msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);

    fetchBankVerifyHistory({
      ...filters,
      hasInvalidEndDate: false,
      offset,
    });

    if (persistFiltersInUrl) {
      updateQueryParams({
        activityType: 'bankVerifyHistory',
      });
    }
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(filters.startDate > filters.endDate);
  }, [filters.startDate, filters.endDate]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const bankVerifyHistoryRequest = {
      StartDateTime: filters.startDate,
      EndDateTime: filters.endDate,
      Timezone: filters.timeZone,
      RequestStatus: filters.status,
      SearchText: filters.searchTerm,
      VerificationStatus: filters.verificationStatus,
      [accountIDType]: clientAccountId,
    };

    blockingLoader.show();

    try {
      const exportBankVerifyData = await fetchAccountVerificationExport(bankVerifyHistoryRequest);
      setExportList(Object.values(exportBankVerifyData.VerificationRequests || {}));

      switch (exportType) {
        case 'pdf':
          downloadPdfTable('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'csv':
          downloadCsvFile('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'xls':
          downloadXls('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'doc':
          downloadDoc('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'text':
          downloadTxt('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'json':
          downloadJson(
            Object.values(exportBankVerifyData.VerificationRequests || {}),
            t('title', { defaultValue: 'VoPay Verify History' }),
          );

          break;
        case 'xml':
          downloadXml('export-bank-verify-history-table', t('title', { defaultValue: 'VoPay Verify History' }));
          break;
        case 'print':
          printFile('export-bank-verify-history-table');
          break;
        default:
          break;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching bank verify history';
      alertDialog.error('Error', msg);
    } finally {
      blockingLoader.hide();
    }
  };

  const tableProps: vp.TableProps<vp.VerificationRequestsList> = {
    id: 'bank-verify-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'recipientName',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.clientName', { defaultValue: 'Client Name' })}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'name',
        template: ({ data }) => `${data.Details.Credit.FirstName} ${data.Details.Credit.LastName}`,
        onHeaderClick: () => {
          setColumnSwap('email');
        },
      },
      {
        id: 'recipientEmail',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientEmail', { defaultValue: 'Recipient Email' })}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'email',
        template: ({ data }) => data.EmailAddress.Receiver || t('notApplicable', { defaultValue: 'N/A' }),
        onHeaderClick: () => {
          setColumnSwap('name');
        },
      },
      {
        id: 'account',
        header: t('Columns.account', { defaultValue: 'Account' }),
        align: 'left',
        template: ({ data }) => (
          <Box ai="center">
            {data.Details.Bank.FinancialInstitutionId !== null &&
              data.Details.Bank.FinancialInstitutionId !== '' &&
              vp.hasBankImage(data.Details.Bank.FinancialInstitutionId) && (
                <PaymentMethodLogo method="BankAccount" bankId={data.Details.Bank.FinancialInstitutionId} size="32px" />
              )}

            <span>{getMaskedNumber(data.Details.Bank.AccountNumber)}</span>
          </Box>
        ),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus', { defaultValue: 'Request Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Request)}
              label={capitalizeFirstLetter(data.Status.Request) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'verificationStatus',
        header: t('Columns.verificationStatus', { defaultValue: 'Verification Status' }),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Verification)}
              label={capitalizeFirstLetter(data.Status.Verification) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'updatedOn',
        header: t('Columns.updatedOn', { defaultValue: 'Updated On' }),
        align: 'left',
        field: 'LastModified',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status.Request === 'active' && data.Status.Verification === 'unverified' && (
              <Button
                id={`copy-url-${data.VerificationRequestID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl', { defaultValue: 'Copy URL' })}
              </Button>
            )}

            {/* Link-out via the host's onNavigate; hidden when the host can't navigate. */}
            {hostNavigate && (
              <Button
                id={`details-${data.VerificationRequestID}`}
                size="small"
                onClick={() => {
                  hostNavigate(`/bank-verify-history/details/${data.VerificationRequestID}`, { verification: data });
                }}
              >
                {t('details', { defaultValue: 'Details' })}
              </Button>
            )}
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.VerificationRequestsList } => ({
      id: String(item.VerificationRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns.filter((col) => col.id !== 'view');

  const tableExportProps: vp.TableProps<vp.VerificationRequestsList> = {
    ...tableProps,
    id: 'export-bank-verify-history-table',
    columns: exportColumns,
    rows: exportList.map((item): { id: string; data: vp.VerificationRequestsList } => ({
      id: String(item.VerificationRequestID || ''),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />

          <Box ai="center" gap="common">
            <BankVerifyHistoryExport onExport={handleExport} />
            <TableFiltersContainer.Trigger />
          </Box>
        </Box>

        <TableFiltersContainer.Content>
          <Box>
            <BankVerifyHistoryForm
              requestStatus={filters.status}
              timeZone={filters.timeZone}
              verificationStatus={filters.verificationStatus}
              searchTerm={filters.searchTerm}
              hasInvalidEndDate={hasInvalidEndDate}
              shouldShowEndDateError={shouldShowEndDateError}
              setShouldShowEndDateError={setShouldShowEndDateError}
              initialTimeRange={[toDayTimestamp(filters.startDate), toDayTimestamp(filters.endDate)]}
              search={(searchParams) => {
                const next: Filters = {
                  status: searchParams.requestStatus,
                  verificationStatus: searchParams.verificationStatus,
                  searchTerm: searchParams.searchTerm,
                  startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                  endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                  timeZone: searchParams.timeZone,
                };

                if (persistFiltersInUrl) {
                  updateQueryParams({
                    status: next.status,
                    verificationStatus: next.verificationStatus,
                    searchTerm: next.searchTerm,
                    startDate: next.startDate,
                    endDate: next.endDate,
                    timeZone: next.timeZone,
                  });
                }

                setFilters(next);

                fetchBankVerifyHistory({
                  ...next,
                  hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                  offset: setPageIndex(0),
                });
              }}
            />
          </Box>
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div style={{ display: 'none' }}>
        <Table {...tableExportProps} />
      </div>

      {fetching ? <Skeleton height="200px" /> : <Table {...tableProps} />}

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);

              fetchBankVerifyHistory({
                ...filters,
                hasInvalidEndDate: filters.startDate > filters.endDate,
                offset,
              });
            }}
          />
        )}
      </Box>

      {!fetching && tableProps.rows && tableProps.rows.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('noRecordMessage', { defaultValue: 'There are no records to display within this date range' })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default BankVerifyHistoryTable;
